/**
 * Provider Configuration Form dialog component.
 * Provides form for creating and editing LLM provider configurations
 * with conditional fields based on provider type.
 */

import { useState, useEffect, useMemo } from 'react';
import { Button, CoordioDropdown, FormField, Input, Switch } from '@coordio/ui';
import { FormSection, InlineErrorPanel } from '@coordio/ui/layout';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import { Eye as VisibilityIcon, EyeOff as VisibilityOffIcon } from 'lucide-react';
import { llmService } from '../../services/llmService';
import { useNotificationStore } from '../../store/notificationStore';
import type { LlmProviderDto, LlmProviderConfigDto } from '../../types/llm';

interface ProviderConfigFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  config: LlmProviderConfigDto | null;
  providers: LlmProviderDto[];
  /**
   * Set of provider IDs that already have a configuration for the current tenant.
   * When adding a new config, those providers are shown as disabled options with
   * a hint, so users understand why a provider (e.g. OpenAI) is unavailable
   * instead of having it silently missing from the dropdown.
   */
  configuredProviderIds?: Set<string>;
}

/** Default xAI endpoint, prefilled so a saved xAI key never silently targets api.openai.com. */
const XAI_DEFAULT_BASE_URL = 'https://api.x.ai/v1';

/** Default Google Gemini OpenAI-compatible endpoint, prefilled for the same reason as xAI. */
const GOOGLE_DEFAULT_BASE_URL = 'https://generativelanguage.googleapis.com/v1beta/openai/';

/**
 * Determines which fields to show based on the provider name.
 */
const getProviderFields = (providerName: string) => {
  const name = providerName.toLowerCase();
  if (name.includes('azure')) {
    return {
      showBaseUrl: true,
      showDeploymentName: true,
      showApiVersion: true,
      showOrganizationId: false,
      showProjectId: true,
      showSecretKey: false,
      baseUrlLabel: 'Azure OpenAI Endpoint',
      baseUrlPlaceholder: 'https://your-resource.openai.azure.com/',
      apiKeyLabel: 'API Key',
      deploymentNamePlaceholder: 'gpt-4o',
      apiVersionPlaceholder: '2024-08-01-preview',
    };
  }
  if (name.includes('amazon') || name.includes('bedrock')) {
    return {
      showBaseUrl: true,
      showDeploymentName: false,
      showApiVersion: false,
      showOrganizationId: false,
      showProjectId: false,
      showSecretKey: true,
      baseUrlLabel: 'AWS Region',
      baseUrlPlaceholder: 'e.g. us-east-1',
      apiKeyLabel: 'Access Key ID',
      deploymentNamePlaceholder: '',
      apiVersionPlaceholder: '',
    };
  }
  if (name.includes('openai') && !name.includes('azure')) {
    return {
      showBaseUrl: true,
      showDeploymentName: false,
      showApiVersion: false,
      showOrganizationId: true,
      showProjectId: true,
      showSecretKey: false,
      baseUrlLabel: 'Base URL (optional)',
      baseUrlPlaceholder: 'https://api.openai.com/v1',
      apiKeyLabel: 'API Key',
      deploymentNamePlaceholder: '',
      apiVersionPlaceholder: '',
    };
  }
  if (name.includes('anthropic')) {
    return {
      showBaseUrl: true,
      showDeploymentName: false,
      showApiVersion: true,
      showOrganizationId: false,
      showProjectId: false,
      showSecretKey: false,
      baseUrlLabel: 'Base URL (optional)',
      baseUrlPlaceholder: 'https://api.anthropic.com',
      apiKeyLabel: 'API Key',
      deploymentNamePlaceholder: '',
      apiVersionPlaceholder: '2023-06-01',
    };
  }
  if (name.includes('xai')) {
    return {
      showBaseUrl: true,
      showDeploymentName: false,
      showApiVersion: false,
      showOrganizationId: false,
      showProjectId: false,
      showSecretKey: false,
      baseUrlLabel: 'Base URL',
      baseUrlPlaceholder: XAI_DEFAULT_BASE_URL,
      apiKeyLabel: 'API Key',
      deploymentNamePlaceholder: '',
      apiVersionPlaceholder: '',
    };
  }
  if (name.includes('google') || name.includes('gemini')) {
    return {
      showBaseUrl: true,
      showDeploymentName: false,
      showApiVersion: false,
      showOrganizationId: false,
      showProjectId: false,
      showSecretKey: false,
      baseUrlLabel: 'Base URL',
      baseUrlPlaceholder: GOOGLE_DEFAULT_BASE_URL,
      apiKeyLabel: 'API Key',
      deploymentNamePlaceholder: '',
      apiVersionPlaceholder: '',
    };
  }
  // Default: show all fields
  return {
    showBaseUrl: true,
    showDeploymentName: true,
    showApiVersion: true,
    showOrganizationId: true,
    showProjectId: true,
    showSecretKey: false,
    baseUrlLabel: 'Base URL',
    baseUrlPlaceholder: 'https://api.example.com',
    apiKeyLabel: 'API Key',
    deploymentNamePlaceholder: '',
    apiVersionPlaceholder: '',
  };
};

export const ProviderConfigForm = ({
  open,
  onClose,
  onSaved,
  config,
  providers,
  configuredProviderIds,
}: ProviderConfigFormProps) => {
  const isEdit = !!config;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Form fields
  const [providerId, setProviderId] = useState('');
  const [configName, setConfigName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [deploymentName, setDeploymentName] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      if (config) {
        setProviderId(config.providerId);
        setConfigName(config.name || '');
        setApiKey(''); // Never pre-fill API key
        setSecretKey(''); // Never pre-fill secret key
        setBaseUrl(config.baseUrl || '');
        setOrganizationId(config.organizationId || '');
        setProjectId(config.projectId || '');
        setDeploymentName(config.deploymentName || '');
        setApiVersion(config.apiVersion || '');
        setIsActive(config.isActive);
      } else {
        setProviderId('');
        setConfigName('');
        setApiKey('');
        setSecretKey('');
        setBaseUrl('');
        setOrganizationId('');
        setProjectId('');
        setDeploymentName('');
        setApiVersion('');
        setIsActive(true);
      }
      setError(null);
      setShowApiKey(false);
    }
  }, [open, config]);

  const selectedProvider = useMemo(
    () => providers.find((p) => p.id === providerId),
    [providers, providerId]
  );

  const providerFields = useMemo(
    () => getProviderFields(selectedProvider?.displayName || ''),
    [selectedProvider]
  );

  /** Build dropdown options with disabled state for already-configured providers. */
  const providerOptions = useMemo(
    () =>
      providers.map((p) => {
        const alreadyConfigured = !isEdit && (configuredProviderIds?.has(p.id) ?? false);
        return {
          value: p.id,
          label: p.displayName,
          badge: `${p.modelCount} models`,
          description: alreadyConfigured ? 'Already configured (edit existing)' : undefined,
          disabled: alreadyConfigured,
        };
      }),
    [providers, isEdit, configuredProviderIds]
  );

  /** Handles provider selection and adjusts the base URL for xAI / Google defaults. */
  const handleProviderChange = (newProviderId: string | null) => {
    if (!newProviderId) return;
    setProviderId(newProviderId);

    const newProvider = providers.find((p) => p.id === newProviderId);
    const newName = (newProvider?.displayName || '').toLowerCase();
    const isXai = newName.includes('xai');
    const isGoogle = newName.includes('google') || newName.includes('gemini');

    let nextUrl = baseUrl;
    if (
      (!isXai && nextUrl === XAI_DEFAULT_BASE_URL) ||
      (!isGoogle && nextUrl === GOOGLE_DEFAULT_BASE_URL)
    ) {
      nextUrl = '';
    }
    if (isXai && !nextUrl) {
      nextUrl = XAI_DEFAULT_BASE_URL;
    } else if (isGoogle && !nextUrl) {
      nextUrl = GOOGLE_DEFAULT_BASE_URL;
    }
    if (nextUrl !== baseUrl) {
      setBaseUrl(nextUrl);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!providerId) {
      setError('Please select a provider.');
      return;
    }

    // For Amazon Bedrock, build metadataJson from secret key when provided
    const metadataJson =
      providerFields.showSecretKey && secretKey
        ? JSON.stringify({ secretAccessKey: secretKey })
        : undefined;

    setSaving(true);
    try {
      if (isEdit && config) {
        await llmService.updateProviderConfig(config.id, {
          name: configName.trim() || undefined,
          apiKey: apiKey || undefined, // undefined means no change
          baseUrl: baseUrl || undefined,
          organizationId: organizationId || undefined,
          projectId: projectId || undefined,
          deploymentName: deploymentName || undefined,
          apiVersion: apiVersion || undefined,
          isActive,
          metadataJson: providerFields.showSecretKey ? (secretKey ? metadataJson : undefined) : undefined,
        });
        addNotification('Provider configuration updated successfully', 'success');
      } else {
        await llmService.createProviderConfig({
          providerId,
          name: configName.trim() || undefined,
          apiKey: apiKey || undefined,
          baseUrl: baseUrl || undefined,
          organizationId: organizationId || undefined,
          projectId: projectId || undefined,
          deploymentName: deploymentName || undefined,
          apiVersion: apiVersion || undefined,
          metadataJson,
        });
        addNotification('Provider configuration created successfully', 'success');
      }
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save provider configuration');
    } finally {
      setSaving(false);
    }
  };

  const { addNotification } = useNotificationStore();

  return (
    <CortexDialog open={open} onClose={onClose} title={isEdit ? `Edit ${config?.providerName} Configuration` : 'Add Provider Configuration'} size="lg" data-testid="provider-config-dialog">
      <CortexDialogBody>
        <div className="mt-2 flex flex-col gap-6">
          {error && (
            <InlineErrorPanel
              message={error}
              action={
                <Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>
                  Dismiss
                </Button>
              }
            />
          )}

          <FormSection title="Provider" description="Select the LLM provider to configure." data-testid="provider-config-provider-section">
            <FormField label="Provider" required description="The LLM provider for this configuration." data-testid="provider-config-provider">
              <CoordioDropdown
                options={providerOptions}
                value={providerId || null}
                onChange={handleProviderChange}
                placeholder="Select a provider"
                disabled={isEdit}
                clearable={false}
                data-testid="provider-config-provider"
              />
            </FormField>
          </FormSection>

          {providerId && (
            <FormSection title="Connection settings" description="API credentials and endpoint configuration for this provider." data-testid="provider-config-connection-section">
              <FormField label="Name" description="Optional friendly name to identify this configuration." data-testid="provider-config-name">
                <Input
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                  placeholder="e.g. Production OpenAI"
                  data-testid="provider-config-name"
                />
              </FormField>

              <div className="mt-5">
                <FormField
                  label={providerFields.apiKeyLabel}
                  description={
                    providerFields.showSecretKey
                      ? 'Optional when using IAM role or environment credentials'
                      : isEdit && config?.hasApiKey
                        ? config?.apiKeyLast5
                          ? `Current key ends with \u2026${config.apiKeyLast5}. Leave empty to keep it.`
                          : 'Leave empty to keep the existing key'
                        : 'Your API key will be stored securely'
                  }
                  data-testid="provider-config-apiKey"
                >
                  <div className="relative">
                    <Input
                      className="w-full pr-10"
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder={
                        providerFields.showSecretKey
                          ? 'e.g. AKIA...'
                          : isEdit && config?.hasApiKey
                            ? '(unchanged - enter new key to update)'
                            : 'Enter API key'
                      }
                      data-testid="provider-config-apiKey"
                    />
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowApiKey(!showApiKey)}
                        type="button"
                      >
                        {showApiKey ? <VisibilityOffIcon size={18} /> : <VisibilityIcon size={18} />}
                      </Button>
                    </div>
                  </div>
                </FormField>
              </div>

              {providerFields.showSecretKey && (
                <div className="mt-5">
                  <FormField label="Secret Access Key" description="Leave empty if using IAM role or AWS environment variables." data-testid="provider-config-secretKey">
                    <div className="relative">
                      <Input
                        className="w-full pr-10"
                        type={showApiKey ? 'text' : 'password'}
                        value={secretKey}
                        onChange={(e) => setSecretKey(e.target.value)}
                        placeholder={isEdit ? '(unchanged - enter new secret to update)' : 'Optional when using IAM or env credentials'}
                        data-testid="provider-config-secretKey"
                      />
                      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowApiKey(!showApiKey)}
                          type="button"
                        >
                          {showApiKey ? <VisibilityOffIcon size={18} /> : <VisibilityIcon size={18} />}
                        </Button>
                      </div>
                    </div>
                  </FormField>
                </div>
              )}

              {providerFields.showBaseUrl && (
                <div className="mt-5">
                  <FormField label={providerFields.baseUrlLabel} description="Provider API endpoint URL." data-testid="provider-config-baseUrl">
                    <Input
                      value={baseUrl}
                      onChange={(e) => setBaseUrl(e.target.value)}
                      placeholder={providerFields.baseUrlPlaceholder}
                      data-testid="provider-config-baseUrl"
                    />
                  </FormField>
                </div>
              )}

              {providerFields.showOrganizationId && (
                <div className="mt-5">
                  <FormField label="Organization ID" description="Optional — Your OpenAI organization ID." data-testid="provider-config-organizationId">
                    <Input
                      value={organizationId}
                      onChange={(e) => setOrganizationId(e.target.value)}
                      placeholder="org-xxxxxxxx"
                      data-testid="provider-config-organizationId"
                    />
                  </FormField>
                </div>
              )}

              {providerFields.showProjectId && (
                <div className="mt-5">
                  <FormField label="Project ID" description="Optional project or resource group identifier." data-testid="provider-config-projectId">
                    <Input
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                      placeholder={selectedProvider?.displayName.includes('Azure') ? 'Azure resource group or project' : 'Project ID'}
                      data-testid="provider-config-projectId"
                    />
                  </FormField>
                </div>
              )}

              {providerFields.showDeploymentName && (
                <div className="mt-5">
                  <FormField label="Deployment Name" description="The name of your model deployment." data-testid="provider-config-deploymentName">
                    <Input
                      value={deploymentName}
                      onChange={(e) => setDeploymentName(e.target.value)}
                      placeholder={providerFields.deploymentNamePlaceholder}
                      data-testid="provider-config-deploymentName"
                    />
                  </FormField>
                </div>
              )}

              {providerFields.showApiVersion && (
                <div className="mt-5">
                  <FormField label="API Version" description="Optional — Specific API version to use." data-testid="provider-config-apiVersion">
                    <Input
                      value={apiVersion}
                      onChange={(e) => setApiVersion(e.target.value)}
                      placeholder={providerFields.apiVersionPlaceholder}
                      data-testid="provider-config-apiVersion"
                    />
                  </FormField>
                </div>
              )}

              {isEdit && (
                <div className="mt-5">
                  <div className="ui-form-choice-field">
                    <div className="ui-form-choice-field__row">
                      <div className="ui-form-choice-field__control">
                        <Switch
                          checked={isActive}
                          onCheckedChange={(checked) => setIsActive(checked)}
                          data-testid="provider-config-isActive"
                        />
                      </div>
                      <div className="ui-form-choice-field__label-cell">
                        <span className="ui-form-choice-field__label">Active</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </FormSection>
          )}
        </div>
      </CortexDialogBody>
      <CortexDialogFooter>
        <Button variant="outline" onClick={onClose} disabled={saving} data-testid="provider-config-cancel-btn">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving || !providerId}
          data-testid="provider-config-submit-btn"
        >
          {saving ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : isEdit ? 'Update' : 'Create'}
        </Button>
      </CortexDialogFooter>
    </CortexDialog>
  );
};
